// Frozen v2.3.0 render — retained verbatim so `cofactor skill diff` / install can reproduce it.
export const testCasesRenderV2_3_0 = () => `---
name: test-cases
version: 2.3.0
description: Author high-quality test cases as \`.cofactor.md\` specs that compile straight into Cofactor workflows. Invoke when the user asks to write test cases, draft a test plan, plan QA scenarios, decide what to test, or convert a PRD/ticket/design into tests.
allowed-tools: Read, Grep, Glob, Bash, Write
---

# Test Case Authoring (Cofactor)

Use this skill when the user asks for test cases, a test plan, QA scenarios, "what should we test for X", or wants to turn a PRD / ticket / design into tests. The output is one or more **\`.cofactor.md\` spec files** — each file is one workflow, one journey, one test case. \`cofactor spec compile <file>\` turns them into runnable Cofactor workflows; \`cofactor spec validate <file>\` enforces the structural rules below. (Legacy \`.canary.md\` files still validate and compile; new specs default to \`.cofactor.md\`.)

The skill has three layers:
1. **Industry rubric** — what makes a test case good (ISTQB-aligned).
2. **Spec mapping** — how each rubric piece becomes a section of \`.cofactor.md\`.
3. **Domain Context** — customer-specific sources of truth, filled in once.

Read the Domain Context block at the bottom **first**. If it is unfilled, follow the discovery instructions there before authoring any cases.

---

## 1. The 7-rule rubric

Each case must satisfy all seven. If a rule cannot be satisfied, say so explicitly and explain why — don't silently skip.

### 1. One coherent journey per case (atomic, but not micro)
A case covers a single coherent journey — one user objective, possibly with several verifications along the way. "Atomic" means *coherent*, not *minimal*: if multiple checks share the same setup and naturally flow from one another, they belong in the same case.

- Good (coherent journey, multiple checks): "Onboard an org: create org → invite a user → claim invite → land on dashboard." Each step verifies its own outcome inline.
- Good (single short journey): "Submit a ticket and confirm it appears in the queue."
- Bad (unrelated jobs): "Submit a ticket, edit a user, generate a report." — three journeys, three separate \`.cofactor.md\` files.

**Rule of thumb:** if isolating a check (e.g. "tagging works") would force you to duplicate most of another case's setup, fold it in as an inline assertion on the larger journey instead. Splitting only pays off when the setup is genuinely different or the failure modes are independent.

### 2. Specific, observable expected results
Every assertion must be something a stranger could verify without judgement.
- Good: "URL ends with \`/dashboard\`." / "A row with name \`X\` appears in the table."
- Bad: "The page looks correct." / "It works."

Lint enforces this as **R1**: a spec with zero run-failing (\`fail\`/\`fatal\`) assertions warns.

### 3. Independent and self-sufficient
A case must not depend on another case running first or on long-lived shared state. Provision data inline (\`## Inputs\` + \`## Scenarios\`, or a \`**Seed**\` node) and clean up in \`## Cleanup\`. Use canonical macros (\\$EMAIL(), \\$UUID()) so reruns don't collide.

### 4. Traceable
Every case carries \`requirementRef:\` in frontmatter pointing at the ticket / PRD / design. If you can't find one, flag the gap to the user — untraceable specs rot. Lint **R2** warns when missing.

### 5. Risk-prioritized
Tag each case with a priority via the \`tags:\` array. Use one of: \`p0\`, \`p1\`, \`p2\`.
- **p0** — happy path of the core promise; if this breaks, the feature is broken.
- **p1** — primary edge cases, validation, role variants, common error states.
- **p2** — rare conditions, visual/agentic checks, low-frequency edges.

### 6. Includes preconditions, data, and cleanup
- **Auth/role preconditions** → \`actor:\` in frontmatter.
- **Other preconditions** (feature flags, seeded data, sister flows) → bulleted prose in \`## Preflight\`.
- **Test data** → \`## Inputs\` (with macros) and per-variant \`## Scenarios\`.
- **Teardown** → bulleted prose in \`## Cleanup\`.

### 7. Written as human intent, not UI mechanics
Action titles describe *what the user is trying to do*; the optional \`> blockquote\` underneath gives just enough context for the recorder/agent. Don't write click-by-click sequences.
- Good: \`**Action — Assign the ticket to Support**\`
- Bad: \`**Action — Click 'Assign' dropdown, scroll, click 'Support', click Save**\`

Lint **R9** warns when an action reads as click-by-click.

---

## 2. Coverage checklist

Before handing back a set of specs, sanity-check coverage. For each, either include a case or note why it doesn't apply:
- Happy path (p0)
- Primary edges / boundary inputs
- Negative paths / validation errors
- Permissions and role variants (admin / member / unauthenticated)
- Empty, loading, and error states
- Cross-entity side effects (does this change something visible elsewhere?)
- Cleanup / undo where applicable

Aim for the smallest set that proves the feature works. Five sharp cases beat twenty fuzzy ones.

---

## 3. Authoring \`.cofactor.md\`

\`.cofactor.md\` is one workflow per file. Frontmatter sets identity; H2 sections carry prose and structured config; \`## Journey\` is the linear test itself. The compiler validates structure; lint enforces the rubric semantics above.

Run these for the definitive grammar — do **not** restate them here:

\`\`\`bash
cofactor spec --help               # validate / compile / diff commands
cofactor spec validate <file>      # parse + lint offline; enforce the rules in §1
cofactor spec compile <file>       # create or update the workflow (prints the live URL)
cofactor workflow guide            # node-shape and macro reference
\`\`\`

After \`cofactor spec compile\`, echo the printed \`URL:\` line back to the user verbatim — it points at the workflow you just created (\`https://app.runcofactor.com/flows/<id>\` for prod, \`https://app.dev.trycanary.ai/flows/<id>\` for dev). Don't construct it yourself.

Mapping at a glance:

| Rubric piece                            | Where it lives in \`.cofactor.md\`                                          |
|-----------------------------------------|---------------------------------------------------------------------------|
| Case title                              | \`frontmatter.name\`                                                       |
| Requirement / ticket ref                | \`frontmatter.requirementRef\`                                             |
| Priority (rule 5)                       | \`tags: [p0]\` (or p1/p2) in frontmatter                                   |
| Auth role precondition                  | \`actor: <X>\` + \`authenticated: true\` in frontmatter. \`<X>\` is a **persona** name by default (e.g. \`actor: Admin\` or the explicit \`actor: persona Admin\`), or a specific raw credential via \`actor: credential <name>\` when the case genuinely needs one named account. Personas resolve at run/suite level; raw credentials bind a single fixed account. Legacy \`actor: template Admin\` remains accepted. List personas with \`cofactor persona list --application "<application>"\`; list credentials with \`cofactor credential list\`. |
| Feature flag / seeded data preconditions| Bulleted prose in \`## Preflight\` ("Feature flag X enabled.", "Seed workflow Y published.") |
| One-paragraph user-visible promise      | \`## Spirit\` (blockquote)                                                 |
| Workflow-level declared inputs          | \`## Inputs\` (e.g. \`- foo: \\$EMAIL()\`) — parses to \`workflows.inputs_json\`, surfaces in Workflow Settings → Inputs. Later values may compose earlier value inputs with \`\\$var.<name>\`; self/forward/unknown references are invalid. |
| Per-step bound values                   | \`- field X: Y\` under an Action — populates *that* step's Inputs panel with \`Y\` as the default (compiler mirrors fields → \`dataRequirements\` + \`inputValues\` on the node). Prefer \`## Inputs\` for values shared across actions. |
| Variants (free / enterprise, etc.)      | \`## Scenarios\` with per-scenario \`values:\` / \`credentials:\` overrides  |
| Steps (human intent)                    | \`## Journey\` — \`**Navigate to** …\`, \`**Action — …**\`, \`**Seed** …\`     |
| Inline expected results                 | \`- assert (fatal \\| fail \\| warn): <condition>\` under the action that produced it. \`fatal\` ends the test now (gating preconditions), \`fail\` marks the run failed but keeps going (feature claims), \`warn\` records without failing (cosmetics) |
| Standalone assertion                    | \`**Assert** <condition>\` node (prefer inline when possible)              |
| Role change mid-journey                 | \`**Switch role to** <X>\` — mirrors \`actor:\`. \`<X>\` is a persona name by default (\`**Switch role to** Admin\`), \`persona <name>\` is the explicit persona form (\`**Switch role to** persona Admin\`), \`credential <name>\` binds a specific raw credential (\`**Switch role to** credential admin-bob\`). Legacy \`template <name>\` remains accepted. Compiles to a login node with \`performLogin: true\`. Repeat as many times as needed in one journey. |
| Cross-action data flow                  | \`- capture: <name>\` on the upstream action, \`\\$var.<name>\` downstream  |
| Cleanup                                 | Bulleted prose in \`## Cleanup\`                                           |

When a journey shares setup and naturally flows end-to-end, emit **one** \`.cofactor.md\` with multiple action nodes and inline assertions. When journeys are unrelated, emit **one file per journey**; if one journey depends on data created by another, link them with \`**Seed** <spec-name>\` or \`**Setup** <spec-name>\` at the top of the dependent file.

### Inputs defaults — the decision rule

When you need a value (workflow-level \`## Inputs\` or step-level \`- field\`), pick where it comes from in this order:

1. **Names existing data in the target environment** — a property name, a credential, a seeded record, an existing org/user/team. **Stop and ask the user for the value before writing the spec.** Don't hand-pick. The right sources of truth:
   - \`cofactor property list\` — properties (target apps)
   - \`cofactor persona list --application "<application>"\` — personas (these are what \`actor:\` and \`- credential …\` reference)
   - \`cofactor workflow list --search <term>\` — existing workflows to seed/link to
   - \`cofactor feature-flag list --env <env>\` — flag names for Preflight prose
2. **Synthesizable** — a new email, a UUID, a throwaway string, an ISO date. Pick a macro (\`\\$EMAIL()\`, \`\\$UUID()\`, \`\\$TODAY()\`) and move on without asking. Parentheses are mandatory, including for zero-argument macros. These render as pills in the Inputs panel and resolve at run time.
3. **Prefer personas** for any auth/role-scoped input. \`actor: Admin\` (frontmatter) and \`- credential admin: Admin\` (inputs) are cleaner than passing \`username:\` + \`password:\` and work across environments. Reach for the raw-credential form (\`actor: credential <name>\`, \`**Switch role to** credential <name>\`, \`- credential admin: <name>\`) only when the case genuinely requires a specific fixed account.

---

## 4. Output format

For each case, write a \`.cofactor.md\` file alongside a one-row summary line. Default location: \`cofactor/specs/<slug>.cofactor.md\` (confirm with the user if their repo uses a different layout — check Domain Context below).

### Summary table

| file                              | name                                       | priority | requirementRef   |
|-----------------------------------|--------------------------------------------|----------|------------------|
| cofactor/specs/submit-ticket.cofactor.md | Submit a ticket and confirm it appears in the queue | p0       | <ticket/PRD ref> |

### Per-case file

\`\`\`markdown
---
name: <one user goal, action-oriented>
actor: <persona name from Domain Context — verbatim, e.g. "Admin"; or \`credential <name>\` for a specific raw credential>
authenticated: true
tags: [p0]            # or p1 / p2; add other tags from Domain Context's tagging conventions
requirementRef: <ticket id, PRD path, design link>
---

# <same as name>

## Spirit
> <one short paragraph: the user-visible promise this case proves>

## Preflight
- <feature flag / seed / sister flow / role precondition, one per bullet>

## Cleanup
- <records to delete, flags to reset>

## Inputs
- <name>: <value or macro>

## Journey

**Navigate to** \\\`/some/path\\\`.

**Action — <human intent>**
> <one-sentence context for the recorder/agent>
- field <name> [(type)]: <value or \\$input>
- capture: <name>        # optional, only if a later step needs it via \\$var.<name>
- assert (fatal): <gating precondition — test must stop if wrong>
- assert (fail): <observable condition — feature is broken if wrong>
- assert (warn): <softer signal — cosmetic, run still passes>

**End**.
\`\`\`

Keep journeys short (3–8 nodes). If a case grows past that, it's probably two cases.

---

## 5. Worked example (generic)

Request: "Test the support ticket flow."

| file                                      | name                                                | priority | requirementRef |
|-------------------------------------------|-----------------------------------------------------|----------|----------------|
| cofactor/specs/submit-ticket.cofactor.md       | Submit a ticket and confirm it appears in the queue | p0       | <fill in>      |
| cofactor/specs/ticket-required-fields.cofactor.md | Required-field validation blocks submit          | p1       | <fill in>      |
| cofactor/specs/ticket-non-admin-reassign.cofactor.md | Non-admin cannot reassign a ticket            | p1       | <fill in>      |

\`cofactor/specs/submit-ticket.cofactor.md\`:

\`\`\`markdown
---
name: Submit a ticket and confirm it appears in the queue
actor: Admin
authenticated: true
tags: [p0, smoke, support]
requirementRef: <fill in>
---

# Submit a ticket and confirm it appears in the queue

## Spirit
> A member can file a support ticket from the in-app form and immediately
> see it land in their team's queue without a refresh.

## Preflight
- At least one Support team exists in the test org (seed workflow \\\`support-team-seed\\\`).
- Feature flag \\\`support_inbox_v3\\\` is enabled.

## Cleanup
- Delete the created ticket.

## Inputs
- subject: "QA \\$UUID()"
- priority: high

## Journey

**Navigate to** \\\`/support/new\\\`.

**Action — Submit the ticket**
> Fill subject and priority, then submit.
- field subject: \\$subject
- field priority (select): \\$priority
- assert (warn): Toast contains "Ticket created"
- assert (fail): URL ends with \\\`/support\\\`

**Navigate to** \\\`/support/queue\\\`.

**Action — Confirm it appears in the queue**
> Locate the new ticket in the team queue.
- assert (fail): A row matching \\\`\\$subject\\\` is visible

**End**.
\`\`\`

After writing, run \`cofactor spec validate cofactor/specs/submit-ticket.cofactor.md\` and resolve any errors / warnings before handing back.

---

## 6. Anti-patterns

- Mega-files that try to cover five unrelated journeys.
- The opposite: splitting one coherent journey into N tiny files that each re-create the same setup. Bundle them into one \`## Journey\` instead.
- Click-by-click action titles ("Click X, scroll, click Y").
- "Looks correct" / "everything works" assertions.
- Hardcoded emails, IDs, or names that collide on rerun. Use macros (\\$EMAIL(), \\$UUID()).
- Specs that depend on another spec having run first without an explicit \`**Seed**\` / \`**Setup**\` node.
- Skipping \`requirementRef\` because "it's obvious".
- Asserting state via toasts only — confirm the underlying record changed.
- Dropping the priority tag — risk-rank every case (\`p0\` / \`p1\` / \`p2\`).

---

## 7. Domain Context

<!-- AGENT/CUSTOMER: fill in this block on first use of the skill, then commit it. -->
<!-- If any field below is "<unfilled>", do a discovery pass before authoring cases: -->
<!--   - Read README.md, CLAUDE.md, AGENTS.md, docs/, specs/ for requirement and design pointers. -->
<!--   - Scan recent git log for ticket-tracker references (JIRA-123, LIN-456, #issue). -->
<!--   - Run \`cofactor property list\` and \`cofactor persona list --application "<application>"\` to learn applications + the personas that map to \`actor:\` values. -->
<!--   - (\`cofactor credential list\` shows live credentials — useful, but the \`actor:\` field references personas, not credentials.) -->
<!--   - Run \`cofactor workflow list --search <feature>\` to avoid duplicating existing workflows. -->
<!--   - Run \`cofactor feature-flag list\` to confirm flag names referenced in Preflight prose. -->
<!--   - Propose values to the user, get confirmation, then edit this block in place. -->

- **Requirements / PRDs**: <unfilled — paths or URLs, e.g. \`docs/specs/\`, Notion space, Linear project>
- **Designs**: <unfilled — Figma links, design system doc>
- **Ticket tracker**: <unfilled — Jira/Linear project key + how to format \`requirementRef\` (e.g. \`PROJ-123\` or a URL)>
- **Default property**: <unfilled — the property name to target; find via \`cofactor property list\`>
- **Default environment**: <unfilled — \`prod\` | \`staging\` | \`dev\`>
- **Credentials / role matrix** (\`actor:\` is a persona name by default — list via \`cofactor persona list --application "<application>"\`. Prefix with \`credential \` to bind a specific raw credential — list via \`cofactor credential list\`. Use the persona form unless the case genuinely needs a fixed named account.): <unfilled — list the persona names available on the default application, with a one-line note on which to use when. Common shape: \`actor: Admin\` (standard user), \`actor: Superadmin\` (full access).>
- **Tagging conventions**: <unfilled — e.g. \`smoke\`, \`regression\`, plus the priority tags \`p0\` / \`p1\` / \`p2\`>
- **Spec directory**: <unfilled — where \`.cofactor.md\` files live in this repo, e.g. \`cofactor/specs/\`>
- **Existing workflows to extend or avoid duplicating**: <unfilled — search via \`cofactor workflow list --search <term>\`>
- **Glossary / domain terms**: <unfilled — terms that show up in tickets and should appear verbatim in spec names>
- **Other sources of truth**: <unfilled — runbooks, support tickets, analytics dashboards>

When this block is fully populated, every case you author should reference at least the **Requirements**, **Default property**, **Spec directory**, and **Credentials** entries.
`;
